<template>
    <v-container fluid class="pb-0">
        <v-toolbar class="elevation-1">
            <v-toolbar-title>
                {{ $t("message.Companies") }}
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn
                v-if="$can('create', 'company')"
                @click="newItem()"
                color="primary"
                class="ml-4"
            >
                {{ $t("message.registerButton") }}
            </v-btn>
            <v-dialog
                @keydown.esc="close()"
                v-model="dialog"
                max-width="800"
                persistent
                :transition="
                    $vuetify.breakpoint.smAndDown
                        ? 'slide-y-transition'
                        : 'scale-transition'
                "
            >
                <v-card class="register-card">
                    <v-card-title>
                        <span class="headline">{{ formTitle }}</span>
                        <v-spacer></v-spacer>
                        <v-btn rounded icon @click="close()">
                            <v-icon medium>cancel</v-icon>
                        </v-btn>
                    </v-card-title>

                    <v-card-text>
                        <v-form
                            @submit.prevent
                            ref="form"
                            @keydown.enter="save"
                        >
                            <v-container grid-list-md>
                                <v-row justify="center" align="center">
                                    <v-col cols="12" md="8">
                                        <v-text-field
                                            outlined
                                            dense
                                            spellcheck="false"
                                            :value="company.name"
                                            @input="
                                                v =>
                                                    (company.name =
                                                        v
                                                            .charAt(0)
                                                            .toUpperCase() +
                                                        v.slice(1))
                                            "
                                            :rules="[
                                                rules.required,
                                                rules.max44Chars,
                                                rules.firstChar,
                                                rules.nameAllowedCharacters,
                                            ]"
                                        >
                                            <template v-slot:label>
                                                {{ headers[0].text }}
                                                <strong style="color: red"
                                                    >*</strong
                                                >
                                            </template>
                                        </v-text-field>
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-form>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn @click="save">
                            <v-icon class="pr-2" color="success">save</v-icon>
                            {{ $t("message.SaveButton") }}
                        </v-btn>
                        <v-btn @click="close">
                            <v-icon class="pr-2" color="error">cancel</v-icon>
                            {{ $t("message.CancelButton") }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-toolbar>
        <v-data-table
            :headers="headers"
            :loading="$store.getters.getLoading"
            :items="$store.getters.getCompanies"
            :search.sync="$store.getters.getSearch"
            :lang="this.$i18n.locale"
            class="elevation-5"
            :items-per-page="35"
            sort-by="name"
            height="calc(100vh - 220px - 40px)"
            fixed-header
            mobile-breakpoint="0"
            :footer-props="$store.getters.getFooterProps"
        >
            <template slot="item" slot-scope="props">
                <tr
                    :style="$can('update', 'company') ? 'cursor:pointer' : ''"
                    @click="
                        $can('update', 'company') ? editItem(props.item) : ''
                    "
                    class="text-no-wrap"
                >
                    <td>{{ props.item.name }}</td>
                    <td v-if="$can('delete', 'company')" class="text-center">
                        <v-btn
                            small
                            class="caption font-weight-black"
                            @click.stop="deleteItem(props.item)"
                        >
                            <v-icon dark color="error" class="pr-2"
                                >delete</v-icon
                            >
                            {{ $t("message.DeleteButton") }}
                        </v-btn>
                    </td>
                </tr>
            </template>
            <v-alert
                slot="no-results"
                dense
                :value="true"
                class="body-1 my-3"
                color="error"
                icon="warning"
            >
                {{ $t("message.YourSearch") }} "{{ $store.getters.getSearch }}"
                {{ $t("message.NoResults") }}
            </v-alert>
        </v-data-table>
    </v-container>
</template>

<script>
export default {
    data: () => ({
        setting: {
            rowsPerPage: -1,
        },
        dialog: false,
        company: {
            name: "",
        },
        defaultCompany: {
            name: "",
        },
        editedIndex: -1,
        changed: false,
    }),

    computed: {
        rules() {
            return {
                required: value => !!value || this.$t("message.FieldRequired"),
                firstChar: value => {
                    const pattern = /^[A-Z-0-9]*$/;
                    return (
                        (value && pattern.test(value.charAt(0))) ||
                        this.$t("message.FirstCharRule")
                    );
                },
                nameAllowedCharacters: value => {
                    const pattern = /^([a-zA-Z0-9. &()-]+)$/;
                    return (
                        (value && pattern.test(value)) ||
                        this.$t("message.SpecialCharacters")
                    );
                },
                max44Chars: value => {
                    if (value && value.length > 44) {
                        return this.$t("message.Max44Chars");
                    }
                    return true;
                },
            };
        },
        formTitle() {
            return this.editedIndex === -1
                ? this.$t("message.NewCompany")
                : this.$t("message.EditCompany");
        },
        headers() {
            return [
                {
                    text: this.$t("message.Name"),
                    value: "name",
                    sortable: true,
                },
                {
                    text: this.$t("message.Actions"),
                    value: "actions",
                    sortable: false,
                    hidden: !this.$can("delete", "company"),
                    align: "center",
                },
            ].filter(title => !title.hidden);
        },
        isFilledCorrectly() {
            return !(
                this.rules.required(this.company.name) ===
                    this.$t("message.FieldRequired") ||
                this.rules.firstChar(this.company.name) ===
                    this.$t("message.FirstCharRule") ||
                this.rules.nameAllowedCharacters(this.company.name) ===
                    this.$t("message.SpecialCharacters") ||
                this.rules.max44Chars(this.company.name) ===
                    this.$t("message.Max44Chars")
            );
        },
    },
    mounted() {
        this.$store.dispatch("processCompanies");
    },
    watch: {
        company: {
            handler() {
                this.changed = true;
            },
            deep: true,
        },
        dialog(val) {
            if (val) {
                this.changed = false;
                this.setFocus();
            }
        },
    },
    methods: {
        setFocus() {
            setTimeout(() => {
                this.$refs.form.$children[0].focus();
                this.$refs.form.resetValidation();
            }, 0);
        },
        editItem(item) {
            this.editedIndex = this.$store.getters.getCompanies.indexOf(item);
            Object.assign(this.company, this.defaultCompany, item);
            this.dialog = true;
        },
        deleteItem(item) {
            this.$store.commit("popUp", {
                type: "deleteCompany",
                id: item.id,
            });
        },
        close() {
            if (
                this.changed &&
                // TODO: USE Alert component instaed
                // eslint-disable-next-line no-restricted-globals, no-alert
                !confirm("Are you sure ? Any unsaved changes will be lost.")
            ) {
                return;
            }

            this.dialog = false;
        },
        newItem() {
            this.company = Object.assign({}, this.defaultCompany);
            this.editedIndex = -1;
            this.dialog = true;
        },
        save() {
            if (!this.isFilledCorrectly) {
                this.$refs.form.validate();
                return;
            }
            if (this.editedIndex > -1) {
                this.$store.dispatch("editCompany", this.company);
            } else {
                this.$store.dispatch("addCompany", this.company);
            }

            this.dialog = false;
        },
    },
};
</script>
